import { Sparkles, Check } from 'lucide-react';
import type { AgentSkill } from '../types/agent-skill';

interface AgentSkillPickerProps {
  skills: AgentSkill[];
  selectedIds: string[];
  onToggle: (skillId: string) => void;
  emptyHint?: string;
}

/**
 * Multiselect grid of installed Agent Skills. Used to pick skills for a one-off
 * instance launch and to manage project bindings. Modeled on PresetPicker.
 */
export default function AgentSkillPicker({
  skills,
  selectedIds,
  onToggle,
  emptyHint = 'No skills installed',
}: AgentSkillPickerProps) {
  if (skills.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 py-4 text-center text-sm text-slate-400">
        {emptyHint}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {skills.map((skill) => {
        const isSelected = selectedIds.includes(skill.id);
        return (
          <button
            key={skill.id}
            type="button"
            onClick={() => onToggle(skill.id)}
            className={`flex items-start gap-2 rounded-lg border p-2.5 text-left transition-all ${
              isSelected
                ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <span
              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded ${
                isSelected
                  ? 'bg-slate-900 text-white'
                  : 'border border-slate-300'
              }`}
            >
              {isSelected ? (
                <Check className="h-3 w-3" />
              ) : (
                <Sparkles className="h-2.5 w-2.5 text-slate-400" />
              )}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-xs font-medium text-slate-900">
                {skill.name}
              </span>
              <span className="block truncate font-mono text-[10px] text-slate-400">
                {skill.slug}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
